import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { FiCheck } from "react-icons/fi";
import MarketNavbar from "../MarketNav/MarketNavbar";
import MarketplacePage from "../components/MarketplacePage";
import OrderPaymentBadge from "../components/OrderPaymentBadge";
import { AuthContext } from "../../../Context/AuthContext";
import { CartContext } from "../../../Context/CartContext";
import { CHECKOUT_COMPLETED_KEY, GUEST_CHECKOUT_EMAIL_KEY } from "../libs/useCheckout";
import { clearServerCart } from "../libs/cartSyncApi";
import {
  STATUS_STEPS,
  formatShippingMethod,
  formatNgn,
  isPayOnDeliveryOrder,
} from "../libs/orderDisplayUtils";
import "./OrderConfirmation.css";

const fetchOrder = async (orderId, token) => {
  const response = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/order/orders/${orderId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.order;
};

const fetchGuestOrder = async (orderId, email) => {
  const response = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/order/guest-confirmation/${orderId}`,
    { params: { email: email.trim().toLowerCase() } }
  );
  return response.data.order;
};

const getItemImage = (item) =>
  item.image || item.product?.images?.[0] || "/placeholder-product.png";

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const { token, isLoggedIn } = useContext(AuthContext);
  const { clearAllCart } = useContext(CartContext);
  const queryClient = useQueryClient();
  const [guestEmail, setGuestEmail] = useState(
    () => sessionStorage.getItem(GUEST_CHECKOUT_EMAIL_KEY) || ""
  );
  const [emailInput, setEmailInput] = useState(guestEmail);
  const [emailSubmitted, setEmailSubmitted] = useState(!!guestEmail);

  const isGuestFlow = !isLoggedIn || !token;

  const { data: order, isLoading, isError, error, refetch } = useQuery(
    ["order", orderId, isGuestFlow ? emailSubmitted : "auth"],
    () =>
      isGuestFlow
        ? fetchGuestOrder(orderId, guestEmail)
        : fetchOrder(orderId, token),
    {
      enabled:
        !!orderId &&
        (isGuestFlow ? emailSubmitted && !!guestEmail : !!token),
      retry: false,
    }
  );

  useEffect(() => {
    if (order && token) {
      queryClient.invalidateQueries("customerInbox");
      queryClient.invalidateQueries("myOrders");
    }
  }, [order, queryClient, token]);

  useEffect(() => {
    if (!order || !sessionStorage.getItem(CHECKOUT_COMPLETED_KEY)) return;
    sessionStorage.removeItem(CHECKOUT_COMPLETED_KEY);
    clearAllCart();
    if (token) {
      clearServerCart(token).catch(() => {});
    }
  }, [order, clearAllCart, token]);

  const handleGuestEmailSubmit = (e) => {
    e.preventDefault();
    const normalized = emailInput.trim().toLowerCase();
    if (!normalized) return;
    sessionStorage.setItem(GUEST_CHECKOUT_EMAIL_KEY, normalized);
    setGuestEmail(normalized);
    setEmailSubmitted(true);
    refetch();
  };

  const currentStep =
    order?.status === "Canceled"
      ? -1
      : order
        ? STATUS_STEPS.indexOf(order.status)
        : -1;

  const timelineProgress =
    currentStep <= 0 ? 0 : (currentStep / (STATUS_STEPS.length - 1)) * 100;

  const subtotal =
    order?.totalAmount != null
      ? order.totalAmount - (order.shippingCost || 0)
      : null;

  const guestEmailMismatch =
    isGuestFlow &&
    emailSubmitted &&
    isError &&
    error?.response?.status === 403;

  const isPod = order && isPayOnDeliveryOrder(order);
  const showLoading =
    (isGuestFlow && emailSubmitted && isLoading) || (!isGuestFlow && isLoading);
  const showError =
    (isGuestFlow && emailSubmitted && isError) || (!isGuestFlow && isError);

  return (
    <>
      <MarketNavbar />
      <MarketplacePage className="marketplace-page--checkout">
        <div className="order-confirm">
          {isGuestFlow && !emailSubmitted && (
            <div className="order-confirm__state">
              <h2>View your order</h2>
              <p>Enter the email you used at checkout to see your order details.</p>
              <form className="order-confirm__state-form" onSubmit={handleGuestEmailSubmit}>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Email address"
                  required
                />
                <button type="submit" className="order-confirm__btn order-confirm__btn--primary">
                  View order
                </button>
              </form>
            </div>
          )}

          {showLoading && (
            <div className="order-confirm__skeleton" aria-hidden="true">
              <div className="order-confirm__skeleton-block" />
              <div className="order-confirm__skeleton-block order-confirm__skeleton-block--tall" />
            </div>
          )}

          {showError && (
            <div className="order-confirm__state">
              <h2>Could not load order</h2>
              <p>
                {guestEmailMismatch
                  ? "That email does not match this order. Check the address from your confirmation email."
                  : isGuestFlow
                    ? "This order may not exist or payment is still processing."
                    : "We could not find this order in your account."}
              </p>
              {isGuestFlow ? (
                <button
                  type="button"
                  className="order-confirm__btn order-confirm__btn--secondary"
                  onClick={() => {
                    setEmailSubmitted(false);
                    setGuestEmail("");
                    sessionStorage.removeItem(GUEST_CHECKOUT_EMAIL_KEY);
                  }}
                >
                  Try a different email
                </button>
              ) : (
                <Link to="/account/orders" className="order-confirm__btn order-confirm__btn--secondary">
                  View my orders
                </Link>
              )}
              <Link
                to="/market/track-order"
                className="order-confirm__btn order-confirm__btn--ghost"
                style={{ marginTop: 8 }}
              >
                Track by order number
              </Link>
            </div>
          )}

          {order && (
            <>
              <header
                className={`order-confirm__hero${isPod ? " order-confirm__hero--pod" : ""}`}
              >
                <div className="order-confirm__hero-icon" aria-hidden="true">
                  <FiCheck />
                </div>
                <div className="order-confirm__hero-body">
                  <h1>
                    {isPod ? "Order placed — pay on delivery" : "Thank you for your order!"}
                  </h1>
                  <p>
                    {isPod
                      ? "We will deliver your items and collect payment on arrival."
                      : "Your order has been received and is being prepared."}
                  </p>
                  <p className="order-confirm__hero-order">
                    Order number: <span>{order.orderNumber}</span>
                  </p>
                  {isPod && order.paymentStatus === "pending" && (
                    <p className="order-confirm__hero-due">
                      Amount due at delivery: <strong>{formatNgn(order.totalAmount)}</strong>
                    </p>
                  )}
                </div>
              </header>

              {order.isGuest && (
                <p className="order-confirm__notice">
                  Guest order — save your order number{" "}
                  <strong>{order.orderNumber}</strong> and check your email for updates.
                </p>
              )}

              {isPod && order.paymentStatus === "pending" && (
                <p className="order-confirm__pod-note">
                  Please have the exact amount ready when your order arrives. Refusing delivery may
                  suspend pay-on-delivery for 60 days.
                </p>
              )}

              <div className="order-confirm__grid">
                <div className="order-confirm__main">
                  {order.status !== "Canceled" && (
                    <section className="order-confirm__card">
                      <h2 className="order-confirm__card-title">Order status</h2>
                      <div className="order-confirm__badges">
                        <span className="order-confirm__status-badge">{order.status}</span>
                        <OrderPaymentBadge order={order} showDue />
                      </div>

                      <div className="order-confirm__timeline" aria-label="Order progress">
                        <div className="order-confirm__timeline-track" aria-hidden="true">
                          <div
                            className="order-confirm__timeline-progress"
                            style={{ width: `${timelineProgress}%` }}
                          />
                        </div>
                        {STATUS_STEPS.map((step, idx) => (
                          <div
                            key={step}
                            className={`order-confirm__timeline-step${
                              idx <= currentStep ? " is-active" : ""
                            }${idx === currentStep ? " is-current" : ""}${
                              idx < currentStep ? " is-complete" : ""
                            }`}
                          >
                            <span className="order-confirm__timeline-dot">
                              {idx < currentStep ? <FiCheck aria-hidden="true" /> : idx + 1}
                            </span>
                            <span className="order-confirm__timeline-label">{step}</span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {order.status === "Canceled" && (
                    <section className="order-confirm__card">
                      <h2 className="order-confirm__card-title">Order status</h2>
                      <span className="order-confirm__status-badge">Canceled</span>
                    </section>
                  )}

                  {order.items?.length > 0 && (
                    <section className="order-confirm__card">
                      <h2 className="order-confirm__card-title">
                        Items ({order.items.length})
                      </h2>
                      <ul className="order-confirm__items">
                        {order.items.map((item) => (
                          <li key={item._id} className="order-confirm__item">
                            <img
                              src={getItemImage(item)}
                              alt=""
                              className="order-confirm__item-img"
                            />
                            <div className="order-confirm__item-body">
                              <span className="order-confirm__item-name">{item.name}</span>
                              <span className="order-confirm__item-meta">
                                Qty {item.quantity}
                                {item.size ? ` · Size ${item.size}` : ""}
                                {" · "}
                                {formatNgn(item.unitPrice)} each
                              </span>
                            </div>
                            <span className="order-confirm__item-price">
                              {formatNgn(item.unitPrice * item.quantity)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {order.shippingAddress && (
                    <section className="order-confirm__card">
                      <h2 className="order-confirm__card-title">Delivery details</h2>
                      <p className="order-confirm__address">
                        <span className="order-confirm__address-name">
                          {order.shippingAddress.full_name}
                        </span>
                        <br />
                        {order.shippingAddress.address}, {order.shippingAddress.city},{" "}
                        {order.shippingAddress.state}
                        <br />
                        {order.shippingAddress.phone_number}
                      </p>
                    </section>
                  )}
                </div>

                <aside className="order-confirm__aside">
                  <section className="order-confirm__card order-confirm__summary">
                    <h2 className="order-confirm__card-title">Order summary</h2>
                    <div className="order-confirm__summary-rows">
                      {subtotal != null && (
                        <div className="order-confirm__summary-row">
                          <span>Subtotal</span>
                          <strong>{formatNgn(subtotal)}</strong>
                        </div>
                      )}
                      <div className="order-confirm__summary-row">
                        <span>Shipping ({formatShippingMethod(order.shippingMethod)})</span>
                        <strong>{formatNgn(order.shippingCost)}</strong>
                      </div>
                    </div>
                    <div className="order-confirm__summary-total">
                      <span>Total</span>
                      <span>{formatNgn(order.totalAmount)}</span>
                    </div>
                  </section>

                  <div className="order-confirm__actions">
                    <Link
                      to={`/market/track-order?orderNumber=${encodeURIComponent(order.orderNumber)}`}
                      className="order-confirm__btn order-confirm__btn--primary"
                    >
                      Track order
                    </Link>
                    {isLoggedIn && token && (
                      <>
                        <Link
                          to={`/account/orders/${orderId}`}
                          className="order-confirm__btn order-confirm__btn--secondary"
                        >
                          View order details
                        </Link>
                        <Link
                          to="/account/orders"
                          className="order-confirm__btn order-confirm__btn--ghost"
                        >
                          View my orders
                        </Link>
                      </>
                    )}
                    {!isLoggedIn && (
                      <Link to="/SignUp" className="order-confirm__btn order-confirm__btn--secondary">
                        Create an account
                      </Link>
                    )}
                    <Link to="/market" className="order-confirm__btn order-confirm__btn--ghost">
                      Continue shopping
                    </Link>
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </MarketplacePage>
    </>
  );
};

export default OrderConfirmation;
